/**
 * Multi-select dropdown built from checkboxes. Renders a button that opens a
 * panel of options; the caller controls the selected `value` array. An empty
 * selection means "all" (the button shows `allLabel`). Closes on outside click
 * or Escape.
 */
import { useEffect, useId, useRef, useState, type ReactElement } from 'react';
import type { SelectOption } from './Select';

export interface MultiSelectProps {
  label?: string;
  options: SelectOption[];
  /** Selected values. Empty = all. */
  value: string[];
  onChange: (value: string[]) => void;
  /** Text shown (and summary) when nothing is selected. */
  allLabel?: string;
  wrapperClassName?: string;
  disabled?: boolean;
}

/** Checkbox-based multi-select with a "select all / clear" toggle. */
export default function MultiSelect({
  label,
  options,
  value,
  onChange,
  allLabel = 'Tất cả',
  wrapperClassName = '',
  disabled = false,
}: MultiSelectProps): ReactElement {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const autoId = useId();

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const selected = new Set(value);
  const toggle = (v: string): void => {
    const next = new Set(selected);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    onChange([...next]);
  };

  const summary =
    value.length === 0
      ? allLabel
      : value.length === 1
        ? (options.find((o) => o.value === value[0])?.label ?? '1 tài khoản')
        : `${value.length} tài khoản`;

  return (
    <div className={wrapperClassName} ref={rootRef}>
      {label && (
        <label htmlFor={autoId} className="label">
          {label}
        </label>
      )}
      <div className="relative">
        <button
          type="button"
          id={autoId}
          disabled={disabled}
          onClick={() => setOpen((o) => !o)}
          className="input flex items-center justify-between gap-2 text-left disabled:cursor-not-allowed disabled:opacity-60"
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          <span className={`truncate ${value.length === 0 ? 'text-gray-500' : 'text-gray-900'}`}>
            {summary}
          </span>
          <svg className="h-4 w-4 shrink-0 text-gray-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
          </svg>
        </button>

        {open && (
          <div className="absolute z-20 mt-1 max-h-72 w-full overflow-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg">
            <button
              type="button"
              onClick={() => onChange([])}
              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <span className={`flex h-4 w-4 items-center justify-center rounded border ${value.length === 0 ? 'border-brand-600 bg-brand-600 text-white' : 'border-gray-300'}`}>
                {value.length === 0 && (
                  <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 011.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z" clipRule="evenodd" /></svg>
                )}
              </span>
              <span className="font-medium">{allLabel}</span>
            </button>
            <div className="my-1 border-t border-gray-100" />
            {options.map((opt) => {
              const isChecked = selected.has(opt.value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => toggle(opt.value)}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                >
                  <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${isChecked ? 'border-brand-600 bg-brand-600 text-white' : 'border-gray-300'}`}>
                    {isChecked && (
                      <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 011.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z" clipRule="evenodd" /></svg>
                    )}
                  </span>
                  <span className="truncate">{opt.label}</span>
                </button>
              );
            })}
            {options.length === 0 && (
              <div className="px-3 py-2 text-sm text-gray-400">Không có tài khoản</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
